import { useEffect } from 'react';
import Autolinker from 'autolinker';
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import { closeOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';

import { Capacitor } from '@capacitor/core';
import { IonCol, IonGrid, IonIcon, IonModal, IonRow, IonText } from '@ionic/react';

import '../first-alerts.scss';

import { useCapturePostHog } from '../../../hooks/useCapturePostHog';
import { setGuestAlertNotification } from '../../../pages/guest/firstAlertsSlice';
import { useAppDispatch, useAppSelector } from '../../../store';
import { TSButton } from '../../atomic/TSButton/TSButton';

export const FirstAlertModal = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { capturePostHogCustomEvent } = useCapturePostHog();
  const guestAlertNotification = useAppSelector((state) => state.firstAlertsSlice.guestAlertNotification);
  const sentAt = dayjs(guestAlertNotification?.sent_timestamp).format('dddd, MMMM DD, YYYY');
  const sentBy = guestAlertNotification?.sent_by;
  const msgBody = guestAlertNotification?.body;
  const text = `${guestAlertNotification?.title}\n\n${sentBy}\n${sentAt}\n\n${msgBody}`;

  useEffect(() => {
    if (guestAlertNotification) {
      capturePostHogCustomEvent('FirstAlertModal opened', guestAlertNotification);
    }
  }, [capturePostHogCustomEvent, guestAlertNotification]);

  const share = async () => {
    if (Capacitor.isPluginAvailable('Share')) {
      const { Share } = await import('@capacitor/share');
      await Share.share({
        title: guestAlertNotification?.title,
        text,
        dialogTitle: t('guestAlertPage.share'),
      });
    }
    capturePostHogCustomEvent('FirstAlertModal shared');
    return false;
  };

  const close = () => {
    dispatch(setGuestAlertNotification(undefined));
    capturePostHogCustomEvent('FirstAlertModal closed');
  };

  if (!guestAlertNotification) return;

  return (
    <IonModal
      id="alert-dialog"
      handle={false}
      isOpen={Boolean(guestAlertNotification)}
      showBackdrop={true}
      backdropDismiss={false}
    >
      <IonRow className="ion-justify-content-end">
        <TSButton variant="clear-black" className="ion-no-padding" onClick={close}>
          <IonIcon slot="icon-only" icon={closeOutline} />
        </TSButton>
      </IonRow>
      <div className="ion-padding-end ion-padding-horizontal">
        <IonRow>
          <IonText className="alert-dialog-heading">
            <h1>{guestAlertNotification?.title}</h1>
          </IonText>
        </IonRow>
        <IonRow>
          <IonText className="alert-dialog-subtitle">{sentBy}</IonText>
        </IonRow>
        <IonRow>
          <IonText className="alert-dialog-subtitle">{sentAt}</IonText>
        </IonRow>
        <IonGrid className="ion-no-padding">
          <IonRow>
            <IonCol className="ion-padding-vertical">
              <IonText>
                {msgBody && (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(Autolinker.link(msgBody, { stripPrefix: false })),
                    }}
                  />
                )}
              </IonText>
            </IonCol>
          </IonRow>
        </IonGrid>
        <IonRow className="ion-justify-content-center ion-padding-bottom">
          <TSButton variant="black" className="ion-no-margin ion-text-capitalize" onClick={share}>
            {t('guestAlertPage.share')}
          </TSButton>
        </IonRow>
        <IonRow className="ion-justify-content-center ion-padding-bottom">
          <TSButton variant="white" className="ion-text-capitalize ion-no-margin" onClick={close}>
            {t('generic.close')}
          </TSButton>
        </IonRow>
      </div>
    </IonModal>
  );
};
